import React, { createContext, useContext, useReducer, useMemo } from 'react';
import sideBar6 from '../assets/utils/images/sidebar/city1.jpg';

// State shape
interface ThemeState {
  backgroundColor: string;
  headerBackgroundColor: string;
  enableMobileMenuSmall: string;
  enableBackgroundImage: boolean;
  enableClosedSidebar: boolean;
  enableFixedHeader: boolean;
  enableHeaderShadow: boolean;
  enableSidebarShadow: boolean;
  enableFixedFooter: boolean;
  enableFixedSidebar: boolean;
  colorScheme: string;
  backgroundImage: string;
  backgroundImageOpacity: string;
  enablePageTitleIcon: boolean;
  enablePageTitleSubheading: boolean;
  enablePageTabsAlt: boolean;
  enableMobileMenu: boolean;
  closedSmallerSidebar: boolean;
}

const initialState: ThemeState = {
  backgroundColor: '',
  headerBackgroundColor: '',
  enableMobileMenuSmall: '',
  enableBackgroundImage: false,
  enableClosedSidebar: false,
  enableFixedHeader: true,
  enableHeaderShadow: true,
  enableSidebarShadow: true,
  enableFixedFooter: true,
  enableFixedSidebar: true,
  colorScheme: 'white',
  backgroundImage: sideBar6,
  backgroundImageOpacity: 'opacity-06',
  enablePageTitleIcon: true,
  enablePageTitleSubheading: true,
  enablePageTabsAlt: true,
  enableMobileMenu: false,
  closedSmallerSidebar: false,
};

// Actions
type ThemeAction =
  | { type: 'SET_ENABLE_BACKGROUND_IMAGE'; enableBackgroundImage: boolean }
  | { type: 'SET_ENABLE_FIXED_HEADER'; enableFixedHeader: boolean }
  | { type: 'SET_ENABLE_HEADER_SHADOW'; enableHeaderShadow: boolean }
  | { type: 'SET_ENABLE_SIDEBAR_SHADOW'; enableSidebarShadow: boolean }
  | { type: 'SET_ENABLE_PAGETITLE_ICON'; enablePageTitleIcon: boolean }
  | {
      type: 'SET_ENABLE_PAGETITLE_SUBHEADING';
      enablePageTitleSubheading: boolean;
    }
  | { type: 'SET_ENABLE_PAGE_TABS_ALT'; enablePageTabsAlt: boolean }
  | { type: 'SET_ENABLE_FIXED_SIDEBAR'; enableFixedSidebar: boolean }
  | { type: 'SET_ENABLE_MOBILE_MENU'; enableMobileMenu: boolean }
  | { type: 'SET_ENABLE_MOBILE_MENU_SMALL'; enableMobileMenuSmall: string }
  | { type: 'SET_ENABLE_CLOSED_SIDEBAR'; enableClosedSidebar: boolean }
  | { type: 'SET_ENABLE_FIXED_FOOTER'; enableFixedFooter: boolean }
  | { type: 'SET_BACKGROUND_COLOR'; backgroundColor: string }
  | { type: 'SET_HEADER_BACKGROUND_COLOR'; headerBackgroundColor: string }
  | { type: 'SET_COLOR_SCHEME'; colorScheme: string }
  | { type: 'SET_BACKGROUND_IMAGE'; backgroundImage: string }
  | { type: 'SET_BACKGROUND_IMAGE_OPACITY'; backgroundImageOpacity: string };

function themeReducer(state: ThemeState, action: ThemeAction): ThemeState {
  switch (action.type) {
    case 'SET_ENABLE_BACKGROUND_IMAGE':
      return { ...state, enableBackgroundImage: action.enableBackgroundImage };
    case 'SET_ENABLE_FIXED_HEADER':
      return { ...state, enableFixedHeader: action.enableFixedHeader };
    case 'SET_ENABLE_HEADER_SHADOW':
      return { ...state, enableHeaderShadow: action.enableHeaderShadow };
    case 'SET_ENABLE_SIDEBAR_SHADOW':
      return { ...state, enableSidebarShadow: action.enableSidebarShadow };
    case 'SET_ENABLE_PAGETITLE_ICON':
      return { ...state, enablePageTitleIcon: action.enablePageTitleIcon };
    case 'SET_ENABLE_PAGETITLE_SUBHEADING':
      return {
        ...state,
        enablePageTitleSubheading: action.enablePageTitleSubheading,
      };
    case 'SET_ENABLE_PAGE_TABS_ALT':
      return { ...state, enablePageTabsAlt: action.enablePageTabsAlt };
    case 'SET_ENABLE_FIXED_SIDEBAR':
      return { ...state, enableFixedSidebar: action.enableFixedSidebar };
    case 'SET_ENABLE_MOBILE_MENU':
      return { ...state, enableMobileMenu: action.enableMobileMenu };
    case 'SET_ENABLE_MOBILE_MENU_SMALL':
      return { ...state, enableMobileMenuSmall: action.enableMobileMenuSmall };
    case 'SET_ENABLE_CLOSED_SIDEBAR':
      return { ...state, enableClosedSidebar: action.enableClosedSidebar };
    case 'SET_ENABLE_FIXED_FOOTER':
      return { ...state, enableFixedFooter: action.enableFixedFooter };
    case 'SET_BACKGROUND_COLOR':
      return { ...state, backgroundColor: action.backgroundColor };
    case 'SET_HEADER_BACKGROUND_COLOR':
      return { ...state, headerBackgroundColor: action.headerBackgroundColor };
    case 'SET_COLOR_SCHEME':
      return { ...state, colorScheme: action.colorScheme };
    case 'SET_BACKGROUND_IMAGE':
      return { ...state, backgroundImage: action.backgroundImage };
    case 'SET_BACKGROUND_IMAGE_OPACITY':
      return {
        ...state,
        backgroundImageOpacity: action.backgroundImageOpacity,
      };
    default:
      return state;
  }
}

// Context
interface ThemeContextValue {
  state: ThemeState;
  setEnableMobileMenu: (enable: boolean) => void;
  setEnableClosedSidebar: (enable: boolean) => void;
  setEnableMobileMenuSmall: (enable: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(themeReducer, initialState);

  const value = useMemo<ThemeContextValue>(
    () => ({
      state,
      setEnableMobileMenu: (enable: boolean) =>
        dispatch({ type: 'SET_ENABLE_MOBILE_MENU', enableMobileMenu: enable }),
      setEnableClosedSidebar: (enable: boolean) =>
        dispatch({
          type: 'SET_ENABLE_CLOSED_SIDEBAR',
          enableClosedSidebar: enable,
        }),
      setEnableMobileMenuSmall: (enable: string) =>
        dispatch({
          type: 'SET_ENABLE_MOBILE_MENU_SMALL',
          enableMobileMenuSmall: enable,
        }),
    }),
    [state],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
